export const bluePalette = {
  100: '#EAEBFE',
  200: '#D4D8FD',
  300: '#BFC4FC', //was lightBlue.100
  400: '#AAB1FB', //was dark.blue200
  500: '#949DFA',
  600: '#7F89FA',
  700: '#5462F8',
  800: '#2A3BF6',
  900: '#0A1DE9', //was mango.blue
  1000: '#0818C2',
  1100: '#07139B',
  1200: '#050E74',
  1300: '#030A4E',
  1400: '#020527',
}

export const grayPalette = {
  100: '#FAFAFC',
  200: '#F6F6FA',
  300: '#EEEEF4',
  400: '#E5E5EE',
  500: '#D3D3E2', //was orange.200
  600: '#B4B4CC', //was dark600
  700: '#9A9AB8', //was orange.400
  800: '#7A7A99', //was dark200, and dark700
  900: '#58587A', //was dark300, and dark800
  1000: '#40405C',
  1100: '#30304B',
  1200: '#27273D',
  1300: '#1B1B33', //was dark900, and orange.800
  1400: '#171726',
  1500: '#0C0C14',
  1600: '#1D1D30',
}

export const greenPalette = {
  100: '#E9FAF6',
  200: '#D4F6ED', //was mint.100
  300: '#A8EDDC',
  400: '#7DE3CA',
  500: '#51DAB8',
  600: '#2BCBA4',
  700: '#22A081',
  800: '#1C856C',
  900: '#176B56',
  1000: '#115041',
  1100: '#0B352B',
  1200: '#061B16',
}

export const redPalette = {
  100: '#FEEDEE', //was orange.100, and red.alert
  200: '#FCDBDD', //was red.focus
  300: '#F9B6BC',
  400: '#F6919A',
  500: '#F36D78',
  600: '#F04957',
  700: '#ED2435',
  800: '#D21122',
  900: '#A80E1B',
  1000: '#7E0A14',
  1100: '#54070D',
  1200: '#2A0307',
}

export const mangoPalette = {
  purple: '#61057F',
  aqua: '#0BF9F2',
  teal: '#5AB1E1',
  blue: bluePalette[900],
  softBlue: bluePalette[700],
  softPurple: '#45025B',
  highlight: '#FFF6C2', //was yellow.200
  orange: '#FF8A00',
}

type ColorMask = {
  10: string
  20: string
  30: string
  40: string
  50: string
  60: string
  70: string
  80: string
  90: string
  100: string
}

export const whiteMask = {
  10: '#FFFFFF1A',
  20: '#FFFFFF33',
  30: '#FFFFFF4D',
  40: '#FFFFFF66',
  50: '#FFFFFF80',
  60: '#FFFFFF99',
  70: '#FFFFFFB3',
  80: '#FFFFFFCC',
  90: '#FFFFFFE6',
  100: '#FFFFFFFF',
}

export const blackMask = {
  10: '#0000001A',
  20: '#00000033',
  30: '#0000004D',
  40: '#00000066',
  50: '#00000080',
  60: '#00000099',
  70: '#000000B3',
  80: '#000000CC',
  90: '#000000E6',
  100: '#000000FF',
}

type BlueMask = Converge<typeof bluePalette, ColorMask>
type GrayMask = Converge<typeof grayPalette, ColorMask>
type GreenMask = Converge<typeof greenPalette, ColorMask>
type RedMask = Converge<typeof redPalette, ColorMask>
type MangoMask = Converge<typeof mangoPalette, ColorMask>

type ColorMasks = {
  white: ColorMask
  black: ColorMask
  blue: BlueMask
  gray: GrayMask
  green: GreenMask
  red: RedMask
  mango: MangoMask
}

const buildPaletteColorMasks = <T extends Record<string, unknown>>(
  paletteObj: T,
): Converge<T, ColorMask> => {
  const colorMasks = {}
  Object.keys(paletteObj).forEach((paletteKey) => {
    const color = paletteObj[paletteKey]
    colorMasks[paletteKey] = {}
    for (let i = 10; i <= 100; i += 10) {
      // Convert to rgb 255 value
      const opacityRgb = Math.round((i / 100) * 255)
      // Convert to hex
      const opacityHex = opacityRgb.toString(16).padStart(2, '0')
      colorMasks[paletteKey][i] = `${color}${opacityHex}`
    }
  })

  return colorMasks as Converge<T, ColorMask>
}

const blueMasks = buildPaletteColorMasks(bluePalette) as BlueMask
const redMasks = buildPaletteColorMasks(redPalette) as RedMask
const greenMasks = buildPaletteColorMasks(greenPalette) as GreenMask
const grayMasks = buildPaletteColorMasks(grayPalette) as GrayMask
const mangoMasks = buildPaletteColorMasks(mangoPalette) as MangoMask

// Mask color keys, without the opacity values
export type ColorMaskKeys =
  | `blue.${keyof typeof bluePalette}`
  | `red.${keyof typeof redPalette}`
  | `green.${keyof typeof greenPalette}`
  | `gray.${keyof typeof grayPalette}`
  | `mango.${keyof typeof mangoPalette}`

export const gradients = {
  mangoBrand: {
    notification_banner_default: `linear-gradient(90deg, ${mangoPalette.purple} 0%, ${bluePalette[900]} 59%, ${mangoPalette.aqua} 99%)`,
    new_action_fab_button_default: `linear-gradient(120deg, hsl(285deg 92% 26%) 0%, hsl(265deg 100% 38%) 39%, hsl(209deg 100% 50%) 61%, hsl(178deg 95% 51%) 100%)`,
    call_sound_indicator_default: `linear-gradient(90deg, ${bluePalette[1100]} 0%, ${mangoPalette.purple} 100%)`,
    button: `linear-gradient(135deg, ${mangoPalette.purple} 19.69%, ${mangoPalette.blue} 57.36%, ${mangoPalette.aqua} 87.57%)`,
  },
}

export const transparent = 'transparent'
export const inherit = 'inherit'
export const none = 'none'

export interface ColorPalette {
  blue: typeof bluePalette
  gray: typeof grayPalette
  green: typeof greenPalette
  red: typeof redPalette
  white: string
  black: string
  mango: typeof mangoPalette
  mask: ColorMasks
  gradients: typeof gradients
  achromatic: {
    transparent: typeof transparent
    inherit: typeof inherit
    none: typeof none
  }
  opacity: typeof opacity
}

export const opacity = {
  0: '0',
  10: '0.1',
  20: '0.2',
  30: '0.3',
  40: '0.4',
  50: '0.5',
  60: '0.6',
  70: '0.7',
  80: '0.8',
  90: '0.9',
  100: '1.0',
}

export const colorPalette: ColorPalette = {
  blue: {
    ...bluePalette,
  },
  gray: {
    ...grayPalette,
  },
  green: {
    ...greenPalette,
  },
  red: {
    ...redPalette,
  },
  white: '#FFFFFF',
  black: '#000000',
  mango: {
    ...mangoPalette,
  },
  mask: {
    white: {
      ...whiteMask,
    },
    black: {
      ...blackMask,
    },
    blue: {
      ...blueMasks,
    },
    gray: {
      ...grayMasks,
    },
    green: {
      ...greenMasks,
    },
    red: {
      ...redMasks,
    },
    mango: {
      ...mangoMasks,
    },
  },
  gradients: {
    ...gradients,
  },
  achromatic: {
    transparent,
    inherit,
    none,
  },
  opacity,
}

export type ColorPaletteKeys = DeepKeys<ColorPalette>
